import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dropzone from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import spinner from '../assign-prescriptions/images/spinner.gif';
import { toast } from 'react-toastify';

const SignatureFlagsSection = ({ accountId, operation, t }) => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);

  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedFlagForForm, setSelectedFlagForForm] = useState(null);
  const [formFile, setFormFile] = useState(null);

  const [savingFlag, setSavingFlag] = useState(false);
  const [uploadingForm, setUploadingForm] = useState(false);
  const [deletingFlagId, setDeletingFlagId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flagPendingDelete, setFlagPendingDelete] = useState(null);

  const isView = operation === 'View';

  const getAuthHeaders = () => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;
    return {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    };
  };

  const loadFlags = useCallback(() => {
    if (!accountId) {
      setFlags([]);
      return;
    }
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BASEURL}accounts/${accountId}/signature-flags`,
      {
        method: 'GET',
        withCredentials: true,
        headers: getAuthHeaders(),
      },
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setFlags(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setFlags([]);
      })
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const openAddFlag = () => {
    setEditingFlag({
      id: null,
      label: '',
      displayName: '',
      externalCodes: '',
      kioskContext: true,
      counterContext: true,
      active: true,
    });
    setFlagModalOpen(true);
  };

  const openEditFlag = (flag) => {
    setEditingFlag({
      id: flag.id,
      label: flag.label || '',
      displayName: flag.displayName || '',
      externalCodes: (flag.externalCodes || []).join(','),
      kioskContext: flag && flag.requiredContexts && flag.requiredContexts.includes('kiosk_pickup'),
      counterContext: flag && flag.requiredContexts && flag.requiredContexts.includes('counter_pickup'),
      active: flag.active !== false,
    });
    setFlagModalOpen(true);
  };

  const closeFlagModal = () => {
    setFlagModalOpen(false);
    setEditingFlag(null);
  };

  const saveFlag = (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!editingFlag || !accountId) {
      toast.error('Unable to save flag – missing account or flag.');
      return;
    }

    const payload = {
      label: (editingFlag.label || '').trim(),
      displayName: (editingFlag.displayName || '').trim(),
      externalCodes: (editingFlag.externalCodes || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean),
      requiredContexts: [
        editingFlag.kioskContext && 'kiosk_pickup',
        editingFlag.counterContext && 'counter_pickup',
      ].filter(Boolean),
      active: !!editingFlag.active,
    };

    if (!payload.label) {
      toast.error('Flag label is required.');
      return;
    }

    const isEdit = !!editingFlag.id;
    const url =
      `${process.env.REACT_APP_BASEURL}accounts/${accountId}/signature-flags` +
      (isEdit ? `/${editingFlag.id}` : '');

    setSavingFlag(true);
    fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      withCredentials: true,
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then(() => {
        toast.success('Signature flag saved.');
        closeFlagModal();
        loadFlags();
      })
      .catch(() => {
        toast.error('Unable to save signature flag.');
      })
      .finally(() => {
        setSavingFlag(false);
      });
  };

  const openFormModal = (flag) => {
    setSelectedFlagForForm(flag);
    setFormFile(null);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setSelectedFlagForForm(null);
    setFormFile(null);
  };

  const handleFormDrop = (acceptedFiles) => {
    if (!acceptedFiles || !acceptedFiles.length) return;
    setFormFile(acceptedFiles[0]);
  };

  const uploadForm = (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!accountId || !selectedFlagForForm || !formFile) {
      toast.error('Missing data to upload form.');
      return;
    }

    const user = JSON.parse(sessionStorage.getItem('user'));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;

    const formData = new FormData();
    formData.append('file', formFile);

    setUploadingForm(true);
    fetch(
      `${process.env.REACT_APP_BASEURL}accounts/${accountId}/signature-flags/${selectedFlagForForm.id}/form`,
      {
        method: 'POST',
        withCredentials: true,
        headers: {
          Authorization: 'Bearer ' + token,
        },
        body: formData,
      },
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then(() => {
        toast.success('Form uploaded.');
        closeFormModal();
        loadFlags();
      })
      .catch(() => {
        toast.error('Unable to upload form.');
      })
      .finally(() => {
        setUploadingForm(false);
      });
  };

  const openDeleteDialog = (flag) => {
    if (!flag || !flag.id) return;
    setFlagPendingDelete(flag);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingFlagId) return;
    setDeleteDialogOpen(false);
    setFlagPendingDelete(null);
  };

  const deleteFlag = () => {
    if (!flagPendingDelete || !flagPendingDelete.id || !accountId) {
      return;
    }

    const flag = flagPendingDelete;
    setDeletingFlagId(flag.id);
    fetch(
      `${process.env.REACT_APP_BASEURL}accounts/${accountId}/signature-flags/${flag.id}`,
      {
        method: 'DELETE',
        withCredentials: true,
        headers: getAuthHeaders(),
      },
    )
      .then((res) => {
        if (res.ok) return;
        return Promise.reject(res);
      })
      .then(() => {
        toast.success('Signature flag deleted.');
        setDeleteDialogOpen(false);
        setFlagPendingDelete(null);
        loadFlags();
      })
      .catch(() => {
        toast.error('Unable to delete signature flag.');
      })
      .finally(() => {
        setDeletingFlagId(null);
      });
  };

  const previewForm = (flag) => {
    if (!accountId || !flag || !flag.id || !flag.formVersion) {
      toast.error('No form available to preview.');
      return;
    }

    // Use uploaded AWS PDF URL directly when available (no blob, no API fetch)
    if (flag.formUrl) {
      window.open(flag.formUrl, '_blank', 'noopener');
      return;
    }

    // Fallback for forms uploaded before file_url existed: fetch via API
    const formApiUrl = `${process.env.REACT_APP_BASEURL}accounts/${accountId}/signature-flags/${flag.id}/form`;
    fetch(formApiUrl, {
      method: 'GET',
      credentials: 'omit',
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) return Promise.reject(res);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
      })
      .catch(() => {
        toast.error('Unable to preview form.');
      });
  };

  return (
    <div>
      <DialogTitle id='signature-flags-title' style={{ paddingTop: '24px' }}>
        {t('Accounts.Terms.SignatureFlagsTitle')}
        <p style={{ fontSize: '0.8rem', color: '#777', marginTop: 4 }}>
          {t('Accounts.Terms.SignatureFlagsDescription')}
        </p>
      </DialogTitle>

      <DialogContent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={openAddFlag}
            disabled={isView}
          >
            {t('Accounts.Terms.SignatureFlags.AddFlag')}
          </Button>
        </div>

        {loading ? (
          <div style={{ padding: 16 }}>
            <img src={spinner} alt='loading' />
          </div>
        ) : flags && flags.length ? (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>{t('Accounts.Terms.SignatureFlags.Label')}</TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.DisplayName')}
                </TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.ExternalCodes')}
                </TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.Contexts')}
                </TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.FormVersion')}
                </TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.Active')}
                </TableCell>
                <TableCell>
                  {t('Accounts.Terms.SignatureFlags.Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flags.map((flag) => (
                <TableRow key={flag.id}>
                  <TableCell>{flag.label}</TableCell>
                  <TableCell>{flag.displayName}</TableCell>
                  <TableCell>
                    {(flag.externalCodes || []).join(', ')}
                  </TableCell>
                  <TableCell>
                    {(flag.requiredContexts || []).join(', ')}
                  </TableCell>
                  <TableCell>{flag.formVersion || '-'}</TableCell>
                  <TableCell>
                    <Switch
                      checked={flag.active !== false}
                      disabled
                      color='primary'
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={t('Accounts.Terms.SignatureFlags.EditFlag')}
                      arrow
                    >
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => openEditFlag(flag)}
                          disabled={isView}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t('Accounts.Terms.SignatureFlags.UploadForm')}
                      arrow
                    >
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => openFormModal(flag)}
                          disabled={isView}
                        >
                          <CloudUploadIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t('Accounts.Terms.SignatureFlags.PreviewForm')}
                      arrow
                    >
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => previewForm(flag)}
                          disabled={isView || !flag.formVersion}
                          color='primary'
                        >
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t('Accounts.Terms.SignatureFlags.DeleteFlag')}
                      arrow
                    >
                      <span>
                        <IconButton
                          size='small'
                          onClick={() => openDeleteDialog(flag)}
                          disabled={isView}
                          color='error'
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant='body2' color='textSecondary'>
            {t('Accounts.Terms.SignatureFlags.NoFlags')}
          </Typography>
        )}
      </DialogContent>

      {/* Add / Edit Flag Modal */}
      <Dialog open={flagModalOpen} onClose={closeFlagModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          {editingFlag && editingFlag.id
            ? t('Accounts.Terms.SignatureFlags.EditFlag')
            : t('Accounts.Terms.SignatureFlags.AddFlag')}
        </DialogTitle>
        <DialogContent>
          {editingFlag && (
            <div>
              <TextField
                margin='normal'
                label={t('Accounts.Terms.SignatureFlags.Label')}
                fullWidth
                value={editingFlag.label}
                onChange={(e) =>
                  setEditingFlag({ ...editingFlag, label: e.target.value })
                }
              />
              <TextField
                margin='normal'
                label={t('Accounts.Terms.SignatureFlags.DisplayName')}
                fullWidth
                value={editingFlag.displayName}
                onChange={(e) =>
                  setEditingFlag({
                    ...editingFlag,
                    displayName: e.target.value,
                  })
                }
              />
              <TextField
                margin='normal'
                label={t('Accounts.Terms.SignatureFlags.ExternalCodes')}
                helperText='Comma-separated Epic flag codes'
                fullWidth
                value={editingFlag.externalCodes}
                onChange={(e) =>
                  setEditingFlag({
                    ...editingFlag,
                    externalCodes: e.target.value,
                  })
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingFlag.kioskContext}
                    onChange={(e) =>
                      setEditingFlag({
                        ...editingFlag,
                        kioskContext: e.target.checked,
                      })
                    }
                  />
                }
                label={t('Accounts.Terms.SignatureFlags.Context.Kiosk')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingFlag.counterContext}
                    onChange={(e) =>
                      setEditingFlag({
                        ...editingFlag,
                        counterContext: e.target.checked,
                      })
                    }
                  />
                }
                label={t('Accounts.Terms.SignatureFlags.Context.Counter')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingFlag.active}
                    onChange={(e) =>
                      setEditingFlag({
                        ...editingFlag,
                        active: e.target.checked,
                      })
                    }
                  />
                }
                label={t('Accounts.Terms.SignatureFlags.Active')}
              />
              {editingFlag.id && (
                <div style={{ marginTop: 16 }}>
                  <Typography variant='subtitle2' gutterBottom>
                    {t('Accounts.Terms.SignatureFlags.FormVersion')}:{' '}
                    {(() => {
                      const existing = flags.find((f) => f.id === editingFlag.id);
                      return existing && existing.formVersion
                        ? existing.formVersion
                        : '-';
                    })()}
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => {
                      const existing = flags.find((f) => f.id === editingFlag.id);
                      if (existing) {
                        openFormModal(existing);
                      }
                    }}
                  >
                    {t('Accounts.Terms.SignatureFlags.UploadForm')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFlagModal} disabled={savingFlag}>
            {t('Accounts.Terms.SignatureFlags.Cancel')}
          </Button>
          <Button color='primary' onClick={saveFlag} disabled={savingFlag}>
            {savingFlag
              ? t('Global.Common.Saving') || 'Saving...'
              : t('Accounts.Terms.SignatureFlags.Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Form Modal */}
      <Dialog open={formModalOpen} onClose={closeFormModal} maxWidth='sm' fullWidth>
        <DialogTitle>
          {t('Accounts.Terms.SignatureFlags.FormDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Accounts.Terms.SignatureFlags.FormDialogHelp')}
          </DialogContentText>
          <div style={{ marginTop: 16 }}>
            <Dropzone
              onDrop={handleFormDrop}
              multiple={false}
              accept='.pdf,.doc,.docx'
              className='signature-flag-form-dropzone'
            >
              <div
                style={{
                  border: '2px dashed #ccc',
                  padding: 16,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <CloudUploadIcon style={{ fontSize: 32, marginBottom: 8 }} />
                <div>
                  {formFile
                    ? formFile.name
                    : t('Accounts.Terms.SignatureFlags.FormFile')}
                </div>
              </div>
            </Dropzone>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormModal} disabled={uploadingForm}>
            {t('Accounts.Terms.SignatureFlags.Cancel')}
          </Button>
          <Button
            color='primary'
            onClick={uploadForm}
            disabled={!formFile || uploadingForm}
          >
            {uploadingForm
              ? t('Global.Common.Uploading') || 'Uploading...'
              : t('Accounts.Terms.SignatureFlags.Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Flag Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {t('Accounts.Terms.SignatureFlags.DeleteFlag')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Accounts.Terms.SignatureFlags.DeleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={!!deletingFlagId}>
            {t('Accounts.Terms.SignatureFlags.Cancel')}
          </Button>
          <Button
            color='error'
            onClick={deleteFlag}
            disabled={!!deletingFlagId}
          >
            {deletingFlagId
              ? t('Global.Common.Saving')
              : t('Accounts.Terms.SignatureFlags.DeleteFlag')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

SignatureFlagsSection.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  operation: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default SignatureFlagsSection;




// WEBPACK FOOTER //
// ./src/components/account-list/SignatureFlagsSection.js